import { useCallback, useState } from "react";
import { Button, Modal } from "../components/ui";

export type ConfirmOptions = {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
};

type PendingConfirm = { opts: ConfirmOptions; resolve: (value: boolean) => void };

/**
 * Promise-based confirmation using the app's Modal (replaces native confirm()).
 * Usage:
 *   const { confirm, confirmDialog } = useConfirm();
 *   ...
 *   if (await confirm({ message: "Delete this?", danger: true })) { ... }
 *   return (<>{confirmDialog}...</>);
 */
export function useConfirm() {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback(
    (opts: ConfirmOptions) => new Promise<boolean>((resolve) => setPending({ opts, resolve })),
    [],
  );

  const settle = (result: boolean) => {
    pending?.resolve(result);
    setPending(null);
  };

  const confirmDialog = pending ? (
    <Modal
      onClose={() => settle(false)}
      size="sm"
      title={pending.opts.title ?? "Are you sure?"}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={() => settle(false)}>
            {pending.opts.cancelLabel ?? "Cancel"}
          </Button>
          <Button
            variant={pending.opts.danger ? "danger" : "primary"}
            size="sm"
            onClick={() => settle(true)}
          >
            {pending.opts.confirmLabel ?? "Confirm"}
          </Button>
        </>
      }
    >
      <p className="text-sm text-gray-600">{pending.opts.message}</p>
    </Modal>
  ) : null;

  return { confirm, confirmDialog };
}
